import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Spin,
  Form,
  Modal,
  Table,
  Button,
  Select,
  Result,
  message,
  Flex,
} from "antd";
import get from "lodash/get";
import map from "lodash/map";
import { PlusOutlined } from "@ant-design/icons";

import { fetchCompanies } from "../../redux/actions/companyActions";
import requireAuth from "../../hocs/requireAuth";
import { SUPER_ADMIN_ROLE_ACCESS, ORGANISATION_ADMIN_ROLE_IDs, ORGANISATION_MANAGER_IDs } from "../../constants/userRoles";
import ProductionComponentForm from "../../components/productionComponent/ProductionComponentForm";
import HeaderSelector from "../../components/productionComponent/HeaderSelector";
import { productionComponentColumns } from "../../constants/tableColumns/productionComponent";
import {
  deleteProductionComponents,
  fetchProductionComponents,
} from "../../redux/actions/productionCompActions";
import { DELETED_PRODUCTION_COMPONENT_SUCCESS } from "../../constants/message";

const initialState = {
  company: undefined,
  templateType: undefined,
};

const ProductionComponentScreen = () => {
  const [loading, setLoading] = useState(false);
  const isStaff = useSelector(state => get(state, "auth.isStaff"))
  const [config, setConfig] = useState(null);
  const [templateType, setTemplateType] = useState(undefined);
  const [productionComponents, setProductionComponents] = useState({
    filtered: [],
    showEmpty: false,
    showAdd: false,
  });

  const [productionComponentModal, setProductionComponentModal] = useState({
    visible: false,
    data: undefined,
  });

  const [filter, setFilters] = useState(initialState);
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const setinitialCompany = (company) => {
    setFilters({
      ...filter,
      company,
    });
    setConfig({
      ...config,
      companies: [company],
      template_types: get(company, "template_access_list"),
    });
    form.setFieldsValue({
      Company: get(company, "id"),
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (get(result, "length") === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const toggleModal = (data = undefined) => {
    setProductionComponentModal({
      visible: !get(productionComponentModal, "visible"),
      data,
    });
  };

  const getProductionComponent = (query) => {
    setLoading(true);
    dispatch(fetchProductionComponents(get(filter, "company.id"), query))
      .then((res) => {
        if (get(res, "length") === 0) {
          setLoading(false);
          setTemplateType(undefined);
          setProductionComponents({
            showEmpty: true,
            filtered: [],
            showAdd: true,
          });
        } else {
          setProductionComponents({
            filtered: res,
            showEmpty: false,
            showAdd: true,
          });
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
        setProductionComponents({
          showEmpty: false,
          showAdd: false,
          filtered: [],
        });
      });
  };

  const deleteProductionComponent = (produtionCompid) => {
    setLoading(true);
    dispatch(
      deleteProductionComponents(get(filter, "company.id"), produtionCompid)
    )
      .then((res) => {
        getProductionComponent(get(filter, "company,id"));
        message.success(DELETED_PRODUCTION_COMPONENT_SUCCESS);
        setTemplateType(undefined)
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
  }, []);
  return (
    <div className="p-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <div>
          <p className="text-xl font-medium leading-[30px] text-black-1">
            Production Component
          </p>
        </div>
      </div>

      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full">
        <Spin spinning={loading}>
          <Form form={form} onFinish={() => getProductionComponent()}>
            <HeaderSelector
              form={form}
              filter={filter}
              config={config}
              setTemplateType={setTemplateType}
              setConfig={setConfig}
              setFilters={setFilters}
            />
          </Form>
          <div className={`px-8`}>
            <Flex
              align="end"
              justify="end"
              className="flex flex-col md:flex-row"
              wrap
              gap={16}
            >
              {get(productionComponents, "filtered.length") > 0 && (
                <Select
                  value={templateType}
                  defaultValue={templateType}
                  style={{ minWidth: 250 }}
                  size="middle"
                  placeholder="Select Template Type"
                  options={map(get(config, "template_types"), (template) => ({
                    label: get(template, "name"),
                    value: get(template, "value"),
                  }))}
                  allowClear
                  onChange={(value) => {
                    setTemplateType(value);
                    const query = { type: value };
                    getProductionComponent(query);
                  }}
                />
              )}
              {get(productionComponents, "showAdd") && (
                <Button
                  type="primary"
                  onClick={() => {
                    toggleModal({ type: { value: templateType } });
                  }}
                  icon={<PlusOutlined />}
                >
                  Add Component
                </Button>
              )}
            </Flex>

            {get(productionComponents, "filtered.length") > 0 && (
              <div className="w-full py-6">
                <Table
                  columns={productionComponentColumns(
                    toggleModal,
                    deleteProductionComponent,
                    isStaff
                  )}
                  dataSource={get(productionComponents, "filtered")}
                  size="small"
                  scroll={{ x: "max-content" }}
                  rowKey={(record) => get(record, "id") || crypto.randomUUID()}
                />
              </div>
            )}
          </div>
        </Spin>
        {get(productionComponents, "showEmpty") && (
          <div className="px-8 py-8">
            <Result
              title={`No Production Components exists...!`}
              subTitle="Please try again with another company."
              status="404"
            />
          </div>
        )}
        <Modal
          title={
            get(productionComponentModal, "data.name", undefined)
              ? "Edit Product Component"
              : "Add Product Component"
          }
          open={get(productionComponentModal, "visible")}
          destroyOnHidden
          footer={false}
          onOk={() => {
            getProductionComponent();
          }}
          onCancel={() => {
            toggleModal();
          }}
        >
          <ProductionComponentForm
            toggle={() => {
              toggleModal();
              getProductionComponent();
            }}
            initialValue={get(productionComponentModal, "data")}
            filter={filter}
            config={config}
            setFilters={setFilters}
            setConfig={setConfig}
            setTemplateType={setTemplateType}
          />
        </Modal>
      </div>
    </div>
  );
};

export default requireAuth(ProductionComponentScreen, [
  ...SUPER_ADMIN_ROLE_ACCESS,
  ...ORGANISATION_ADMIN_ROLE_IDs,
  ...ORGANISATION_MANAGER_IDs
]);
